import { useCallback, useEffect, useRef, useState } from 'react';
import { useThrottledCallback } from 'use-debounce';
import { SECOND } from '../constants/time';

const DEFAULT_TIMEOUT = 30 * SECOND;

const eventNames: string[] = [
  'mousedown',
  'mousemove',
  'mousewheel',
  'keydown',
  'touchmove',
  'touchstart',
  'wheel'
];

export function useIdleTimeout(timeout = DEFAULT_TIMEOUT, startImmediately = false) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [idle, setIdle] = useState(false);

  const stop = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
    }
  }, []);

  const start = useCallback(() => {
    stop();

    timer.current = setTimeout(() => {
      setIdle(true);
    }, timeout);
  }, [timeout, stop]);

  const onActivityHandler = useCallback(() => {
    setIdle(false);
    start();
  }, [start]);

  const debouncedActivityHandler = useThrottledCallback(onActivityHandler, 500);

  useEffect(() => {
    eventNames.forEach(eventName => {
      document.addEventListener(eventName, debouncedActivityHandler);
    });

    return () => {
      eventNames.forEach(eventName => {
        document.removeEventListener(eventName, debouncedActivityHandler);
      });
    };
  }, [debouncedActivityHandler]);

  useEffect(() => {
    if (startImmediately) {
      start();
    }

    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    };
  }, [timeout, start, startImmediately]);

  return { idle, start, stop };
}
